/**
 * Payment Service
 * 
 * Handles Stripe checkout flow and credit management.
 */

import { supabase } from '@/lib/supabase';
import type { AspectRatio, LogoPosition, WatermarkPosition } from '@/types';
import type { PlanId, BillingCycle } from '@/lib/stripeConfig';

// ============================================================================
// Types
// ============================================================================

export interface CreateCheckoutResponse {
  url: string | null;
  error?: string;
}

export interface UserCredits {
  credits: number;
  loading: boolean;
  error?: string;
}

export interface Transaction {
  id: string;
  amount: number;
  priceInCents: number;
  status: string;
  packageName: string;
  createdAt: string;
}

// ============================================================================
// Checkout (Abo, Top-up, Einzel-Grundriss)
// ============================================================================

type CheckoutBody = Record<string, unknown>;

/**
 * Shared helper: creates a Stripe Checkout session via the create-checkout
 * Edge Function and redirects the browser to Stripe.
 */
const startCheckout = async (body: CheckoutBody): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Nicht eingeloggt. Bitte melde dich an.' };
    }

    const redirectUrl = window.location.origin + '/app/credits';

    const { data, error } = await supabase.functions.invoke<CreateCheckoutResponse>('create-checkout', {
      body: { ...body, redirectUrl },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Checkout error:', error);
      return { success: false, error: error.message || 'Fehler beim Erstellen der Zahlung' };
    }

    if (!data?.url) {
      return { success: false, error: data?.error || 'Keine Checkout-URL erhalten' };
    }

    // Redirect to Stripe Checkout
    window.location.href = data.url;
    return { success: true };
  } catch (err) {
    console.error('startCheckout error:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unbekannter Fehler',
    };
  }
};

/**
 * Startet den Checkout für ein Bild-Abo (optional mit Grundriss-Paket).
 */
export const startSubscriptionCheckout = async (
  plan: 'starter' | 'pro' | 'agency',
  billingCycle: 'monthly' | 'yearly',
  withFloorplanAddon: boolean
): Promise<{ success: boolean; error?: string }> =>
  startCheckout({ type: 'subscription', plan, billingCycle, withFloorplanAddon });

/**
 * Kauft Bild-Credit-Top-ups (1,50 €/Credit, nur mit aktivem Abo).
 */
export const buyTopupCredits = async (credits: number): Promise<{ success: boolean; error?: string }> =>
  startCheckout({ type: 'topup', credits });

/**
 * Kauft Einzel-Grundrisse (15 €/Stück, kein Abo nötig).
 */
export const buySingleFloorplan = async (quantity = 1): Promise<{ success: boolean; error?: string }> =>
  startCheckout({ type: 'floorplan_single', quantity });

// ============================================================================
// Credit State (Guthaben-Töpfe, Pricing v4)
// ============================================================================

export interface CreditState {
  ownerId: string;
  plan: 'starter' | 'pro' | 'agency' | null;
  billingCycle: 'monthly' | 'yearly' | null;
  subscriptionStatus: string | null;
  grundrissAddon: boolean;
  /** Monats-Bild-Credits (Reset je Abo-Monat) */
  imageCreditsMonth: number;
  /** Top-up-Bild-Credits (verfallen nie) */
  imageCreditsTopup: number;
  /** Summe beider Bild-Töpfe */
  totalImageCredits: number;
  monthlyImageAllowance: number;
  /** Grundriss-Kontingent aus dem Paket (monatlich) */
  floorplanQuotaMonth: number;
  monthlyFloorplanAllowance: number;
  /** Gekaufte Einzel-Grundrisse (verfallen nie) */
  floorplanSingles: number;
  totalFloorplanUnits: number;
  creditsRenewsAt: string | null;
}

const EMPTY_CREDIT_STATE: Omit<CreditState, 'ownerId'> = {
  plan: null,
  billingCycle: null,
  subscriptionStatus: null,
  grundrissAddon: false,
  imageCreditsMonth: 0,
  imageCreditsTopup: 0,
  totalImageCredits: 0,
  monthlyImageAllowance: 0,
  floorplanQuotaMonth: 0,
  monthlyFloorplanAllowance: 0,
  floorplanSingles: 0,
  totalFloorplanUnits: 0,
  creditsRenewsAt: null,
};

/**
 * Liest alle Guthaben-Töpfe über den get_credit_state-RPC.
 * Führt serverseitig den monatlichen Lazy-Refill aus.
 */
export const fetchCreditState = async (userId: string): Promise<CreditState> => {
  try {
    const { data, error } = await supabase.rpc('get_credit_state');
    if (error || !data?.[0]) {
      if (error) console.error('Error fetching credit state:', error);
      return { ownerId: userId, ...EMPTY_CREDIT_STATE };
    }

    const row = data[0];
    return {
      ownerId: row.owner_id,
      plan: row.plan ?? null,
      billingCycle: row.billing_cycle ?? null,
      subscriptionStatus: row.subscription_status ?? null,
      grundrissAddon: row.grundriss_addon ?? false,
      imageCreditsMonth: Number(row.image_credits_month ?? 0),
      imageCreditsTopup: Number(row.image_credits_topup ?? 0),
      totalImageCredits: Number(row.total_image_credits ?? 0),
      monthlyImageAllowance: Number(row.monthly_image_allowance ?? 0),
      floorplanQuotaMonth: Number(row.floorplan_quota_month ?? 0),
      monthlyFloorplanAllowance: Number(row.monthly_floorplan_allowance ?? 0),
      floorplanSingles: Number(row.floorplan_singles ?? 0),
      totalFloorplanUnits: Number(row.total_floorplan_units ?? 0),
      creditsRenewsAt: row.credits_renews_at ?? null,
    };
  } catch (err) {
    console.error('fetchCreditState error:', err);
    return { ownerId: userId, ...EMPTY_CREDIT_STATE };
  }
};

/**
 * Realtime-Subscription auf die Guthaben-Töpfe des Credit-Owners.
 * Liest bei jeder Profil-Änderung den vollen Zustand neu (RPC), damit auch
 * der Agency-Team-Pool korrekt abgebildet wird.
 */
export const subscribeToCreditState = (
  userId: string,
  ownerId: string,
  onChange: (state: CreditState) => void
): (() => void) => {
  const channel = supabase
    .channel(`credit-state:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${ownerId}`,
      },
      async () => {
        const state = await fetchCreditState(userId);
        onChange(state);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

// ============================================================================
// Subscription Management (Grundriss-Add-on, Status)
// ============================================================================

/** Vorgemerkter Plan-Wechsel (Downgrade zum Periodenende), live aus dem Stripe-Schedule. */
export interface ScheduledChange {
  plan: 'starter' | 'pro' | 'agency';
  cycle: 'monthly' | 'yearly';
  grundrissAddon: boolean;
  effectiveDate: string;
}

export interface SubscriptionInfo {
  plan: 'starter' | 'pro' | 'agency' | null;
  billingCycle: 'monthly' | 'yearly' | null;
  subscriptionStatus: string | null;
  grundrissAddon: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  scheduledChange: ScheduledChange | null;
}

const invokeManageSubscription = async <T>(body: Record<string, unknown>): Promise<{ data: T | null; error?: string }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { data: null, error: 'Nicht eingeloggt. Bitte melde dich an.' };
    }

    const { data, error } = await supabase.functions.invoke<T & { error?: string }>('manage-subscription', {
      body,
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (error) {
      return { data: null, error: error.message };
    }
    if ((data as { error?: string })?.error) {
      return { data: null, error: (data as { error?: string }).error };
    }
    return { data: data as T };
  } catch (err) {
    console.error('manage-subscription error:', err);
    return { data: null, error: err instanceof Error ? err.message : 'Unbekannter Fehler' };
  }
};

export const fetchSubscriptionInfo = async (): Promise<{ info: SubscriptionInfo | null; error?: string }> => {
  const { data, error } = await invokeManageSubscription<{
    plan: SubscriptionInfo['plan'];
    billingCycle: SubscriptionInfo['billingCycle'];
    subscriptionStatus: string | null;
    grundrissAddon: boolean;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    scheduledChange: ScheduledChange | null;
  }>({ action: 'status' });

  if (!data) return { info: null, error };
  return { info: { ...data, scheduledChange: data.scheduledChange ?? null } };
};

/**
 * Bucht das Grundriss-Paket zum laufenden Abo dazu bzw. entfernt es.
 */
export const setFloorplanAddon = async (enabled: boolean): Promise<{ success: boolean; error?: string }> => {
  const { data, error } = await invokeManageSubscription<{ ok: boolean }>({
    action: 'set_floorplan_addon',
    enabled,
  });
  return { success: !!data?.ok, error };
};

/**
 * Wechselt das Basis-Abo (Upgrade/Downgrade) nativ über Stripe.
 * Ein aktives Add-on wird serverseitig auf den Zielplan mit-migriert.
 */
export const changePlan = async (
  plan: PlanId,
  cycle: BillingCycle,
  withAddon?: boolean,
): Promise<{ success: boolean; error?: string }> => {
  const { data, error } = await invokeManageSubscription<{ ok: boolean }>({
    action: 'change_plan',
    plan,
    cycle,
    ...(withAddon !== undefined ? { withAddon } : {}),
  });
  return { success: !!data?.ok, error };
};

/**
 * Nimmt einen vorgemerkten Plan-Wechsel (Downgrade zum Periodenende) zurück,
 * indem der Stripe-Subscription-Schedule freigegeben wird.
 */
export const cancelScheduledChange = async (): Promise<{ success: boolean; error?: string }> => {
  const { data, error } = await invokeManageSubscription<{ ok: boolean }>({
    action: 'cancel_scheduled_change',
  });
  return { success: !!data?.ok, error };
};

// ============================================================================
// Credit Balance
// ============================================================================

/**
 * Fetches the current credit balance for a user.
 * 
 * @param userId - The user's ID
 * @returns Credit balance or 0 if not found
 */
export const fetchUserCredits = async (userId: string): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('credits')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching credits:', error);
      return 0;
    }

    return data?.credits ?? 0;
  } catch (err) {
    console.error('fetchUserCredits error:', err);
    return 0;
  }
};

/**
 * Subscribes to credit balance changes via Supabase Realtime.
 * 
 * @param userId - The user's ID
 * @param onCreditsChange - Callback when credits change
 * @returns Unsubscribe function
 */
export const subscribeToCredits = (
  userId: string,
  onCreditsChange: (credits: number) => void
): (() => void) => {
  console.log(`[Credits] Setting up realtime subscription for user ${userId}`);
  
  const channel = supabase
    .channel(`credits:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${userId}`,
      },
      (payload) => {
        const newCredits = (payload.new as { credits?: number })?.credits ?? 0;
        console.log('[Credits] Realtime update received:', newCredits);
        onCreditsChange(newCredits);
      }
    )
    .subscribe((status) => {
      console.log(`[Credits] Subscription status: ${status}`);
    });

  return () => {
    supabase.removeChannel(channel);
  };
};

// ============================================================================
// Transaction History
// ============================================================================

/**
 * Fetches transaction history for a user.
 * 
 * @param userId - The user's ID
 * @returns Array of transactions
 */
export const fetchTransactions = async (userId: string): Promise<Transaction[]> => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }

    return (data || []).map(tx => ({
      id: tx.id,
      amount: tx.amount,
      priceInCents: tx.price_in_cents,
      status: tx.status,
      packageName: tx.package_name || 'Unknown',
      createdAt: tx.created_at,
    }));
  } catch (err) {
    console.error('fetchTransactions error:', err);
    return [];
  }
};

// ============================================================================
// Credit Usage History
// ============================================================================

export interface CreditUsage {
  id: string;
  creditsUsed: number;
  action: string;
  projectId?: string;
  imageId?: string;
  createdAt: string;
}

/**
 * Fetches credit usage history for a user.
 * 
 * @param userId - The user's ID
 * @param limit - Max number of records to fetch
 * @returns Array of usage records
 */
export const fetchCreditUsage = async (userId: string, limit = 50): Promise<CreditUsage[]> => {
  try {
    const { data, error } = await supabase
      .from('credit_usage')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching credit usage:', error);
      return [];
    }

    return (data || []).map(usage => ({
      id: usage.id,
      creditsUsed: usage.credits_used,
      action: usage.action,
      projectId: usage.project_id,
      imageId: usage.image_id,
      createdAt: usage.created_at,
    }));
  } catch (err) {
    console.error('fetchCreditUsage error:', err);
    return [];
  }
};

// ============================================================================
// Ensure Profile Exists
// ============================================================================

/**
 * Ensures a profile exists for the user (creates if missing).
 * Useful for users who signed up before the credit system was implemented.
 * 
 * @param userId - The user's ID
 * @param email - The user's email
 */
export const ensureProfileExists = async (userId: string, email?: string): Promise<void> => {
  try {
    const { data } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .single();

    if (!data) {
      // Create profile with default credits
      const { error } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: email || '',
          credits: 3, // Default starting credits
        });

      if (error && error.code !== '23505') { // Ignore duplicate key errors
        console.error('Error creating profile:', error);
      }
    }
  } catch (err) {
    console.error('ensureProfileExists error:', err);
  }
};

// ============================================================================
// Customer Portal
// ============================================================================

export interface CreatePortalLinkResponse {
  url: string | null;
  error?: string;
}

/**
 * Creates a Stripe Customer Portal session URL.
 * Allows users to manage billing info and view invoices.
 * 
 * @returns Portal URL or error
 */
export const createCustomerPortalLink = async (): Promise<{ url: string | null; error?: string }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { url: null, error: 'Nicht eingeloggt. Bitte melde dich an.' };
    }

    const returnUrl = window.location.origin + '/settings';

    // Explicitly pass the auth token in headers
    const { data, error } = await supabase.functions.invoke<CreatePortalLinkResponse>('create-portal-link', {
      body: { returnUrl },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Portal link error:', error);
      return { url: null, error: error.message || 'Fehler beim Erstellen des Portal-Links' };
    }

    if (!data?.url) {
      return { url: null, error: 'Keine Portal-URL erhalten' };
    }

    return { url: data.url };
  } catch (err) {
    console.error('createCustomerPortalLink error:', err);
    return { 
      url: null, 
      error: err instanceof Error ? err.message : 'Unbekannter Fehler' 
    };
  }
};

// ============================================================================
// Profile Management
// ============================================================================

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  credits: number;
  defaultAspectRatio: AspectRatio | null; // null = ask each time
  defaultDownloadCompressed: boolean; // true = compressed JPEG for portals
  // Default branding/overlay settings
  defaultLogoPosition: LogoPosition;
  defaultWatermarkOpacity: number;
  defaultWatermarkScale: number; // 0.1-1.0 (stored in DB as 10-100)
  defaultWatermarkPosition: WatermarkPosition;
  defaultLogoAssetId: string | null;
  defaultOverlayAssetId: string | null;
  defaultWatermarkAssetId: string | null;
  // Branding settings for tenant upload page
  companyName?: string;
  brandingLogoPath?: string;
  brandingAccentColor?: string;
  // Avatar
  avatarPath: string | null;
  role: string | null;
}

// Type for profile data from Supabase
interface ProfileData {
  id: string;
  email: string | null;
  full_name: string | null;
  credits: number | null;
  default_aspect_ratio?: AspectRatio | null;
  default_download_compressed?: boolean | null;
  default_logo_position?: string | null;
  default_watermark_opacity?: number | null;
  default_watermark_scale?: number | null;
  default_watermark_position?: string | null;
  default_logo_asset_id?: string | null;
  default_overlay_asset_id?: string | null;
  default_watermark_asset_id?: string | null;
  company_name?: string | null;
  branding_logo_path?: string | null;
  branding_accent_color?: string | null;
  avatar_path?: string | null;
  role?: string | null;
}

/**
 * Fetches the user's profile data.
 */
export const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    // Fetch profile with all available fields
    const result = await supabase
      .from('profiles')
      .select('id, email, full_name, credits, default_aspect_ratio, default_download_compressed, default_logo_position, default_watermark_opacity, default_watermark_scale, default_watermark_position, default_logo_asset_id, default_overlay_asset_id, default_watermark_asset_id, company_name, branding_logo_path, branding_accent_color, avatar_path, role')
      .eq('id', userId)
      .single();

    let profileData = result.data as ProfileData | null;
    let queryError = result.error;

    // If some columns don't exist yet, retry with basic fields
    if (queryError && (queryError.message.includes('default_aspect_ratio') || queryError.message.includes('company_name') || queryError.message.includes('branding') || queryError.message.includes('default_logo_position') || queryError.message.includes('default_watermark'))) {
      console.warn('[Profile] Some columns not found. Run migration 009 & 013');
      const fallbackResult = await supabase
        .from('profiles')
        .select('id, email, full_name, credits')
        .eq('id', userId)
        .single();
      
      profileData = fallbackResult.data as ProfileData | null;
      queryError = fallbackResult.error;
    }

    if (queryError) {
      console.error('Error fetching profile:', queryError);
      return null;
    }

    if (!profileData) {
      return null;
    }

    return {
      id: profileData.id,
      email: profileData.email || '',
      fullName: profileData.full_name || '',
      credits: profileData.credits || 0,
      defaultAspectRatio: profileData.default_aspect_ratio ?? null,
      defaultDownloadCompressed: profileData.default_download_compressed ?? true,
      defaultLogoPosition: (profileData.default_logo_position as LogoPosition) || 'bottom-right',
      defaultWatermarkOpacity: profileData.default_watermark_opacity ?? 30,
      defaultWatermarkScale: (profileData.default_watermark_scale ?? 50) / 100,
      defaultWatermarkPosition: (profileData.default_watermark_position as WatermarkPosition) || 'center',
      defaultLogoAssetId: profileData.default_logo_asset_id || null,
      defaultOverlayAssetId: profileData.default_overlay_asset_id || null,
      defaultWatermarkAssetId: profileData.default_watermark_asset_id || null,
      companyName: profileData.company_name || undefined,
      brandingLogoPath: profileData.branding_logo_path || undefined,
      brandingAccentColor: profileData.branding_accent_color || '#6366f1',
      avatarPath: profileData.avatar_path ?? null,
      role: profileData.role || null,
    };
  } catch (err) {
    console.error('fetchUserProfile error:', err);
    return null;
  }
};

/**
 * Completes onboarding: saves profile data, awards the +10 signup bonus credits.
 * Uses SECURITY DEFINER RPC to bypass protect_sensitive_columns trigger.
 */
export const completeOnboarding = async (
  data: { fullName: string; role: string; defaultAspectRatio?: AspectRatio | null }
): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc('complete_onboarding', {
      p_full_name: data.fullName,
      p_role: data.role,
      p_default_aspect_ratio: data.defaultAspectRatio || null,
      p_promo_code: null,
    });

    if (error) {
      console.error('[Onboarding] Error completing onboarding:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('[Onboarding] completeOnboarding error:', err);
    return false;
  }
};

/**
 * Updates the user's profile name.
 */
export const updateUserProfile = async (userId: string, fullName: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ full_name: fullName })
      .eq('id', userId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('updateUserProfile error:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Unbekannter Fehler' 
    };
  }
};

/**
 * Updates the user's default aspect ratio preference.
 * @param aspectRatio - '1:1', '4:3', or null (ask each time)
 */
export const updateDefaultAspectRatio = async (
  userId: string, 
  aspectRatio: AspectRatio | null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ default_aspect_ratio: aspectRatio })
      .eq('id', userId);

    if (error) {
      // If column doesn't exist, warn but don't fail
      if (error.message.includes('default_aspect_ratio')) {
        console.warn('[Profile] default_aspect_ratio column not found. Run migration 009_aspect_ratio.sql');
        return { success: true }; // Silently succeed
      }
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('updateDefaultAspectRatio error:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Unbekannter Fehler' 
    };
  }
};

/**
 * Updates the user's default download compression preference.
 * @param compressed - true = compressed JPEG for portals, false = original PNG
 */
export const updateDefaultDownloadCompressed = async (
  userId: string,
  compressed: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ default_download_compressed: compressed })
      .eq('id', userId);

    if (error) {
      if (error.message.includes('default_download_compressed')) {
        console.warn('[Profile] default_download_compressed column not found. Run migration 006.');
        return { success: true };
      }
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('updateDefaultDownloadCompressed error:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unbekannter Fehler'
    };
  }
};

/**
 * Updates the user's default branding/overlay settings.
 */
export interface DefaultBrandingSettings {
  logoPosition?: LogoPosition;
  watermarkOpacity?: number; // 5-80
  watermarkScale?: number;   // 10-100 (integer)
  watermarkPosition?: WatermarkPosition;
  logoAssetId?: string | null;
  overlayAssetId?: string | null;
  watermarkAssetId?: string | null;
}

export const updateDefaultBrandingDefaults = async (
  userId: string,
  settings: DefaultBrandingSettings
): Promise<{ success: boolean; error?: string }> => {
  try {
    const updateData: Record<string, unknown> = {};

    if (settings.logoPosition !== undefined) {
      updateData.default_logo_position = settings.logoPosition;
    }
    if (settings.watermarkOpacity !== undefined) {
      updateData.default_watermark_opacity = settings.watermarkOpacity;
    }
    if (settings.watermarkScale !== undefined) {
      updateData.default_watermark_scale = settings.watermarkScale;
    }
    if (settings.watermarkPosition !== undefined) {
      updateData.default_watermark_position = settings.watermarkPosition;
    }
    if (settings.logoAssetId !== undefined) {
      updateData.default_logo_asset_id = settings.logoAssetId;
    }
    if (settings.overlayAssetId !== undefined) {
      updateData.default_overlay_asset_id = settings.overlayAssetId;
    }
    if (settings.watermarkAssetId !== undefined) {
      updateData.default_watermark_asset_id = settings.watermarkAssetId;
    }

    const { error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId);

    if (error) {
      if (error.message.includes('default_logo_position') || error.message.includes('default_watermark')) {
        console.warn('[Profile] Default branding columns not found. Run migration 007.');
        return { success: true };
      }
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('updateDefaultBrandingDefaults error:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unbekannter Fehler'
    };
  }
};

/**
 * Updates the user's branding settings for tenant upload page.
 */
export const updateBrandingSettings = async (
  userId: string,
  settings: {
    companyName?: string;
    brandingLogoPath?: string | null;
    brandingAccentColor?: string;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const updateData: Record<string, unknown> = {};
    
    if (settings.companyName !== undefined) {
      updateData.company_name = settings.companyName;
    }
    if (settings.brandingLogoPath !== undefined) {
      updateData.branding_logo_path = settings.brandingLogoPath;
    }
    if (settings.brandingAccentColor !== undefined) {
      updateData.branding_accent_color = settings.brandingAccentColor;
    }

    const { error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId);

    if (error) {
      // If columns don't exist yet, warn
      if (error.message.includes('company_name') || error.message.includes('branding')) {
        console.warn('[Profile] Branding columns not found. Run migration 013_branding_settings.sql');
        return { success: false, error: 'Branding-Felder noch nicht in der Datenbank vorhanden.' };
      }
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('updateBrandingSettings error:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Unbekannter Fehler' 
    };
  }
};

/**
 * Uploads a branding logo and returns the storage path.
 */
export const uploadBrandingLogo = async (
  userId: string,
  file: File
): Promise<{ success: boolean; path?: string; error?: string }> => {
  try {
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
    const fileName = `${userId}/logo_${Date.now()}.${fileExt}`;    const { error: uploadError } = await supabase.storage
      .from('branding')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      return { success: false, error: uploadError.message };
    }

    return { success: true, path: fileName };
  } catch (err) {
    console.error('uploadBrandingLogo error:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Upload fehlgeschlagen' 
    };
  }
};

/**
 * Gets public URL for a branding asset.
 */
export const getBrandingLogoUrl = (path: string): string => {
  const { data } = supabase.storage.from('branding').getPublicUrl(path);
  return data.publicUrl;
};